import React, { memo, useCallback, useEffect, useRef, useState } from "react";

import { Box, Button } from "@chakra-ui/react";
import { ReactComponent as RefreshIcon } from "assets/icons/table-refresh-icon.svg";
import { useIndividualsSavings } from "contexts/individuals-savings-context";
import {
  AVAILABLE_EXPORT_OPTIONS,
  ExportDataEnum,
  useExportData,
} from "hooks/useExportData";
import { useFilterQueryParams } from "hooks/useFilterQueryParams";
import { useUpdateQueryParams } from "hooks/useUpdateQueryParams";
import { debounce } from "lodash";
import { useLocation, useSearchParams } from "react-router-dom";
import { haveQueryParamsChanged } from "utils";
import {
  NO_NEW_FILTER_MESSAGE,
  SEARCH_DEBOUNCE_TIMER,
  ZERO,
} from "utils/constants";

import { customWarningToast } from "ui/custom-toast";
import { Export } from "ui/export";
import { Filter } from "ui/filter";
import {
  IDateRangeValues,
  IFilterRef,
  TFilterOption,
  TFilterValue,
  TFilterValues,
} from "ui/filter/types";
import { tableHeaderCtaCss } from "ui/table/styles";
import { TableSearchInput } from "ui/table/table-search-input";

import {
  ALL_INDIVIDUALS_SAVINGS_FREQUENCY_LIST,
  ALL_INDIVIDUALS_SAVINGS_STATUS_LIST,
  DEFAULT_SAVINGS_TRANSACTIONS_PAGINATION_PARAMS,
} from "../../consts";
import { ITableHeaderCTAsProps } from "../types";

// import { ITableHeaderCTAsProps } from "./types";

const LG_WIDTH = 900;

const filterOptions: TFilterOption[] = [
  {
    type: "checkbox",
    title: "Status",
    key: "status",
    options: [
      {
        label: "All",
        value: "all",
      },
      {
        label: "Active",
        value: "active",
      },
      {
        label: "Completed",
        value: "completed",
      },
      {
        label: "Paused",
        value: "paused",
      },
      {
        label: "Stopped",
        value: "stopped",
      },
      {
        label: "Matured",
        value: "matured",
      },
    ],
  },
  {
    type: "checkbox",
    title: "Frequency",
    key: "frequency",
    options: [
      {
        label: "All",
        value: "all",
      },
      {
        label: "Daily",
        value: "daily",
      },
      {
        label: "Weekly",
        value: "weekly",
      },
      {
        label: "Monthly",
        value: "monthly",
      },
    ],
  },
  {
    type: "date-range",
    title: "Custom Date Range",
    key: "dateRange",
  },
];

interface ISavingsTableHeaderCTAsProps extends ITableHeaderCTAsProps {}

export const TableHeaderCTAs = memo((props: ISavingsTableHeaderCTAsProps) => {
  const { isLoadingTransactions, isRefreshing, handleRefreshTable } = props;
  const ALL_INDIVIDUALS_GOAL_SAVINGS_STATUS_LIST = [
    ...ALL_INDIVIDUALS_SAVINGS_STATUS_LIST,
    "matured",
  ];
  const { individualsTableHeaderCTAWidth } = useIndividualsSavings();
  const updateQueryParams = useUpdateQueryParams();
  const { isFilterError, setIsFilterError, filterRetryCountParams } =
    useFilterQueryParams({
      apiFilterRoute: "/savings/goal/admin/list",
    });
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    isExporting,
    handleExport,
    handleResetExport,
    handleExportTypeChange,
    handleDateRangeChange,
    dateRange,
    exportType,
    dropDownRef,
  } = useExportData({
    type: ExportDataEnum.GOAL_SAVINGS,
  });
  const status = searchParams.get("status");
  const frequency = searchParams.get("frequency");
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const search = searchParams.get("search");
  const [searchQuery, setSearchQuery] = useState(search || "");
  const [filterValues, setFilterValues] = useState<TFilterValues>({
    frequency: frequency ? frequency.split(",") : [],
    status: status ? status.split(",") : [],
    dateRange: {
      startDate,
      endDate,
    },
  });
  const filterRef = useRef<IFilterRef | null>(null);
  const [isFiltering, setIsFiltering] = useState(false);

  const isFilterDisabled = !(
    (filterValues?.dateRange as IDateRangeValues)?.startDate ||
    (filterValues?.frequency as string[])?.length > ZERO ||
    (filterValues?.status as string[])?.length > ZERO
  );

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const handleResetFilter = () => {
    setFilterValues({
      frequency: [],
      status: [],
      dateRange: {
        startDate: null,
        endDate: null,
      },
    });
  };

  const onIndividualGoalSavingsSearch = useCallback(
    debounce(async (value: string) => {
      if (value.trim() === "") {
        updateQueryParams({
          ...Object.fromEntries(new URLSearchParams(location.search)),
          search: null,
          page: String(DEFAULT_SAVINGS_TRANSACTIONS_PAGINATION_PARAMS.page),
          limit: String(DEFAULT_SAVINGS_TRANSACTIONS_PAGINATION_PARAMS.limit),
          isDateFilter: null,
          resetFilter: "normal",
        });
        return;
      }

      updateQueryParams({
        ...Object.fromEntries(new URLSearchParams(location.search)),
        page: String(DEFAULT_SAVINGS_TRANSACTIONS_PAGINATION_PARAMS.page),
        limit: String(DEFAULT_SAVINGS_TRANSACTIONS_PAGINATION_PARAMS.limit),
        search: value,
        isDateFilter: null,
        resetFilter: "normal",
      });
    }, SEARCH_DEBOUNCE_TIMER),
    [location]
  );

  const handleSearchQueryChange = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = evt.target;
    setSearchQuery(value);
    onIndividualGoalSavingsSearch(value);
  };

  const handleFilterValueChange = (key: string, value: TFilterValue) => {
    let newValue = value;
    if (key === "status") {
      const transactionStatus = value as string[];
      const currentTransactionStatus = filterValues?.status as string[];
      if (
        transactionStatus.includes("all") &&
        currentTransactionStatus.length <
          ALL_INDIVIDUALS_GOAL_SAVINGS_STATUS_LIST.length
      ) {
        newValue = ALL_INDIVIDUALS_GOAL_SAVINGS_STATUS_LIST;
      } else {
        newValue = transactionStatus.filter((trxStatus) => trxStatus !== "all");
      }
    } else if (key === "frequency") {
      const savingsFrequency = value as string[];
      const currentSavingsFrequency = filterValues?.frequency as string[];
      if (
        savingsFrequency.includes("all") &&
        currentSavingsFrequency.length <
          ALL_INDIVIDUALS_SAVINGS_FREQUENCY_LIST.length
      ) {
        newValue = ALL_INDIVIDUALS_SAVINGS_FREQUENCY_LIST;
      } else {
        newValue = savingsFrequency.filter((trxStatus) => trxStatus !== "all");
      }
    }
    setFilterValues((prev) => ({
      ...prev,
      [key]: newValue,
    }));
  };

  const handleApplyFilter = async () => {
    const newQueryParams: Record<string, string | null> = {};
    const startDate = (filterValues.dateRange as IDateRangeValues).startDate;
    const endDate = (filterValues.dateRange as IDateRangeValues).endDate;
    const status = filterValues.status;
    const frequency = filterValues.frequency;

    if (startDate) {
      newQueryParams["startDate"] = startDate;
    } else {
      newQueryParams["startDate"] = null;
    }

    if (endDate) {
      newQueryParams["endDate"] = endDate;
    } else {
      newQueryParams["endDate"] = null;
    }
    if (Array.isArray(status) && status.length > ZERO) {
      newQueryParams["status"] = status.join(",");
    } else {
      newQueryParams["status"] = null;
    }

    if (Array.isArray(frequency) && frequency.length > ZERO) {
      newQueryParams["frequency"] = frequency.join(",");
    } else {
      newQueryParams["frequency"] = null;
    }

    newQueryParams["page"] =
      DEFAULT_SAVINGS_TRANSACTIONS_PAGINATION_PARAMS.page.toString();
    newQueryParams["limit"] =
      DEFAULT_SAVINGS_TRANSACTIONS_PAGINATION_PARAMS.limit.toString();
    setIsFilterError(false);
    updateQueryParams({
      ...newQueryParams,
      ...filterRetryCountParams,
      resetFilter: "normal",
      isDateFilter: null,
    });
    if (
      haveQueryParamsChanged({
        currentSearch: location.search,
        newParams: newQueryParams,
      }) ||
      isFilterError
    ) {
      setIsFiltering(true);
    } else {
      customWarningToast({
        message: NO_NEW_FILTER_MESSAGE,
      });
    }
  };

  useEffect(() => {
    if (!isLoadingTransactions && filterRef?.current?.isFilterOpen) {
      setIsFiltering(false);
      filterRef?.current?.handleToggleFilter();
    }
  }, [isLoadingTransactions, filterRef]);

  useEffect(() => {
    if (!isLoadingTransactions && isRefreshing) {
      handleResetFilter();
    }
  }, [isLoadingTransactions]);

  return (
    <Box
      className={`flex p-3 pt-4 ${individualsTableHeaderCTAWidth < LG_WIDTH ? "flex-col justify-evenly items-start" : "items-center justify-between h-[85px]"}`}
      ref={wrapperRef}
    >
      <Box className="flex items-center gap-4">
        <TableSearchInput
          searchQuery={searchQuery}
          handleSearchQueryChange={handleSearchQueryChange}
        />
        <Filter
          options={filterOptions}
          values={filterValues}
          onChange={handleFilterValueChange}
          onApplyFilter={handleApplyFilter}
          filterBtnCss={tableHeaderCtaCss}
          isLoading={isFiltering}
          ref={filterRef}
          isApplyBtnDisabled={isFilterDisabled}
          handleResetFilter={handleResetFilter}
        />
        <Export
          isLoading={isExporting}
          handleExport={handleExport}
          dateRange={dateRange}
          exportType={exportType}
          handleExportTypeChange={handleExportTypeChange}
          handleDateRangeChange={handleDateRangeChange}
          dropDownRef={dropDownRef}
          availableExportOptions={AVAILABLE_EXPORT_OPTIONS}
          handleResetExport={handleResetExport}
        />
        <Button
          css={tableHeaderCtaCss}
          onClick={!isRefreshing ? handleRefreshTable : undefined}
          disabled={isRefreshing}
        >
          <RefreshIcon
            className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
          />
          <p className="text-sm text-[#374151] font-semibold">Refresh</p>
        </Button>
      </Box>
    </Box>
  );
});
